import { audioSession, type AudioSessionClaims } from './session.ts';

/**
 * Echo cancellation matters: without it a microphone open while the tutor
 * speaks (hands-free) hears the tutor. A press stops the tutor before the
 * stream opens, so on push-to-talk the cancellation has nothing to cancel;
 * whether asking for it still costs a Bluetooth profile switch is the device
 * probe's question (media-channel design §3, §4.1).
 */
export const CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    channelCount: 1,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

/**
 * The only place a microphone is opened (§4.1), so the session type cannot
 * drift from whether a stream is live. Record-capable before `getUserMedia`,
 * because the platform reroutes as capture begins.
 */
export async function openMicrophone(
  devices: Pick<MediaDevices, 'getUserMedia'> = navigator.mediaDevices,
  claims: AudioSessionClaims = audioSession,
): Promise<MediaStream> {
  claims.recordingStarted();
  try {
    return await devices.getUserMedia(CONSTRAINTS);
  } catch (error) {
    claims.recordingStopped();
    throw error;
  }
}

export function closeMicrophone(
  stream: Pick<MediaStream, 'getTracks'>,
  claims: AudioSessionClaims = audioSession,
): void {
  for (const track of stream.getTracks()) track.stop();
  claims.recordingStopped();
}
